import React, { useEffect, useMemo, useState } from 'react';

import { Icon, IconClose, IconCalendar } from '@flixbus/honeycomb-icons-react';
import { Calendar, Input, Button } from '@flixbus/honeycomb-react';

import { useOutsideClick } from 'hooks/useClickOutside';
import { CALENDAR_MONTHS, CALENDAR_WEEKDAYS } from 'utils/calendarConfig';
import { formatDateDisplay, isTimestampBefore, areTheSameDates } from 'utils/datetime';

import styles from './styles.module.css';

const DateRangeFilterWA = ({
	value = { fromDate: null, toDate: null },
	onChange = () => {},
	onReset = () => {},
	label = 'Date range',
	placeholder = 'dd.mm.yyyy',
	withIcon = true,
}) => {
	const [showCalendar, setShowCalendar] = useState(false);
	const [startDate, setStartDate] = useState(null);
	const [endDate, setEndDate] = useState(null);

	const ref = useOutsideClick(() => {
		setShowCalendar(false);
	});

	const today = new Date();
	const endMonth = new Date(today.getFullYear(), today.getMonth() + 12, 0);

	const hasCommittedValue = Boolean(value?.fromDate && value?.toDate);

	useEffect(() => {
		if (!hasCommittedValue) {
			setStartDate(null);
			setEndDate(null);
			return;
		}

		setStartDate(value.fromDate ? new Date(value.fromDate) : null);
		setEndDate(value.toDate ? new Date(value.toDate) : null);
	}, [value, hasCommittedValue]);

	const valueShown = useMemo(() => {
		if (!value?.fromDate || !value?.toDate) return '';

		if (areTheSameDates(value.fromDate, value.toDate)) {
			return formatDateDisplay(value.fromDate, { style: 'dotted' });
		}

		return `${formatDateDisplay(value.fromDate, { style: 'dotted' })} - ${formatDateDisplay(value.toDate, { style: 'dotted' })}`;
	}, [value]);

	const resetCalendar = () => {
		setShowCalendar(false);
	};

	const handleSelection = (date) => {
		if (startDate === null) {
			setStartDate(date);
			setEndDate(null);
			return;
		}

		if (endDate === null) {
			if (isTimestampBefore(startDate, date) || startDate === date) {
				setEndDate(date);
				resetCalendar();

				onChange({
					fromDate: startDate.toISOString(),
					toDate: date.toISOString(),
				});

				return;
			}

			setStartDate(date);
			setEndDate(null);
			return;
		}

		setStartDate(date);
		setEndDate(null);
	};

	const handleReset = () => {
		resetCalendar();
		setStartDate(null);
		setEndDate(null);
		onReset();
	};

	const activateCalendar = () => {
		setShowCalendar(true);
	};

	const inputProps = {
		label,
		placeholder,
		id: 'departure-range-input',
		onFocus: activateCalendar,
	};

	if (withIcon) {
		inputProps.iconLeft = (
			<Button type="button" onClick={activateCalendar}>
				<Icon extraClasses={styles.icon} InlineIcon={IconCalendar} />
			</Button>
		);
	}

	if (hasCommittedValue) {
		inputProps.iconRight = (
			<Button type="button" extraClasses={styles.icon} display="square" onClick={handleReset}>
				<Icon extraClasses={styles.icon} InlineIcon={IconClose} />
			</Button>
		);
	}

	return (
		<div className={styles.holder} ref={ref}>
			<Input value={valueShown} {...inputProps} readOnly arial-label="Input calendar" />
			{showCalendar && (
				<div className={styles.calendar}>
					<Calendar
						arial-label="Select calendar"
						id="simple-picker"
						selected={undefined}
						startDate={new Date(2024, 1, 1)}
						endDate={endMonth}
						handleSelect={handleSelection}
						appearance="compact"
						days={CALENDAR_WEEKDAYS}
						months={CALENDAR_MONTHS}
						startSelected={startDate}
						endSelected={endDate}
						showCurrentDate
					/>
				</div>
			)}
		</div>
	);
};

export default DateRangeFilterWA;
